import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { courtTypesApi, extractOne } from '../../api/facades';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const CourtTypeEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = id ? parseInt(id, 10) : NaN;
  const [court_type_name, setCourtTypeName] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const { data: res, isLoading } = useQuery({
    queryKey: ['court-types', numId],
    queryFn: () => courtTypesApi.get(numId),
    enabled: !Number.isNaN(numId),
  });
  const row = extractOne(res);

  useEffect(() => {
    if (!row || typeof row !== 'object') return;
    if ((row as any).court_type_name != null) setCourtTypeName(String((row as any).court_type_name));
    if ((row as any).is_active != null) setIsActive((row as any).is_active === 'No' ? 'No' : 'Yes');
  }, [row]);

  const update = useMutation({
    mutationFn: () => courtTypesApi.update(numId, { court_type_name: court_type_name || undefined, is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-types'] });
      toast.success('Court updated.');
      navigate(`/dashboard/settings/court-types/${numId}`);
    },
    onError: (e: any) => toast.error(e?.message || 'Update failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!court_type_name.trim()) return;
    update.mutate();
  };

  if (Number.isNaN(numId)) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border"><p className="text-slate-600">Invalid ID.</p><Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>Back</Button></div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <h1 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-4">Edit Court</h1>
      {isLoading ? <p className="text-slate-500">Loading…</p> : (
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormField label="Name">
              <input className={inputClass} value={court_type_name} onChange={(e) => setCourtTypeName(e.target.value)} placeholder="e.g. High Court" required />
            </FormField>
          </div>
          <FormField label="Active">
            <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </FormField>
          <div className="md:col-span-2 flex gap-2 mt-2">
            <Button variant="secondary" type="button" onClick={() => navigate(-1)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!court_type_name.trim() || update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CourtTypeEditPage;
